import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import { userService, User, UserCreate, UserUpdate } from '../../services/userService';
import { tenantService } from '../../services/tenantService';
import { useAuthStore } from '../../store/authStore';

interface UserFormProps {
  user?: User | null;
  onClose: () => void;
}

const UserForm = ({ user, onClose }: UserFormProps) => {
  const queryClient = useQueryClient();
  const { user: currentUser } = useAuthStore();
  
  const { data: tenantsData } = useQuery(
    'tenants',
    () => tenantService.getAll(1, 100),
    { enabled: currentUser?.role === 'admin' }
  );

  const isEditing = !!user;

  // Valores iniciales del formulario
  const getInitialValues = () => {
    if (user) {
      return {
        email: user.email || '',
        name: user.name || '',
        role: user.role || 'user',
        tenantId: user.tenantId,
        isActive: user.isActive ?? true,
        password: ''
      };
    }
    return {
      email: '',
      password: '',
      name: '',
      role: 'user',
      tenantId: currentUser?.tenantId || undefined,
      isActive: true
    };
  };

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch
  } = useForm<UserCreate | UserUpdate>({
    defaultValues: getInitialValues(),
    mode: 'onChange'
  });

  const password = watch('password');

  // Resetear solo cuando cambia el usuario (modo edición)
  useEffect(() => {
    if (user) {
      reset(getInitialValues());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const createMutation = useMutation(userService.create, {
    onSuccess: () => {
      toast.success('Usuario creado correctamente');
      queryClient.invalidateQueries(['users']);
      onClose();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error?.message || 'Error al crear usuario');
    }
  });

  const updateMutation = useMutation(
    (data: UserUpdate) => userService.update(user!.id, data),
    {
      onSuccess: () => {
        toast.success('Usuario actualizado correctamente');
        queryClient.invalidateQueries(['users']);
        onClose();
      },
      onError: (error: any) => {
        toast.error(error.response?.data?.error?.message || 'Error al actualizar usuario');
      }
    }
  );

  const onSubmit = (data: UserCreate | UserUpdate) => {
    // Si estamos editando y no hay password, no enviarlo
    if (isEditing && !password) {
      const { password: _, ...dataWithoutPassword } = data as any;
      updateMutation.mutate(dataWithoutPassword);
    } else if (isEditing) {
      updateMutation.mutate(data as UserUpdate);
    } else {
      createMutation.mutate(data as UserCreate);
    }
  };

  const roles = ['admin', 'manager', 'user', 'cashier'];

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" 
      onClick={onClose}
    >
      <div 
        className="bg-white rounded-lg p-6 w-full max-w-md max-h-[90vh] overflow-y-auto" 
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        style={{ pointerEvents: 'auto' }}
      >
        <h2 className="text-xl font-bold mb-4">
          {user ? 'Editar Usuario' : 'Nuevo Usuario'}
        </h2>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {currentUser?.role === 'admin' && tenantsData && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Tenant
              </label>
              <select
                {...register('tenantId', { valueAsNumber: true })}
                disabled={isEditing}
                className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:bg-gray-100"
              >
                {tenantsData.data.tenants.map((tenant) => (
                  <option key={tenant.id} value={tenant.id}>
                    {tenant.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nombre *
            </label>
            <input
              type="text"
              {...register('name', { required: 'Nombre es requerido' })}
              className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary-500 text-gray-900"
              autoComplete="name"
              style={{ pointerEvents: 'auto', userSelect: 'text' }}
            />
            {errors.name && (
              <p className="text-red-500 text-sm mt-1">{errors.name.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email *
            </label>
            <input
              type="email"
              {...register('email', { required: 'Email es requerido' })}
              className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary-500 text-gray-900"
              autoComplete="email"
              style={{ pointerEvents: 'auto', userSelect: 'text' }}
            />
            {errors.email && (
              <p className="text-red-500 text-sm mt-1">{errors.email.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {isEditing ? 'Nueva Contraseña (dejar vacío para no cambiar)' : 'Contraseña *'}
            </label>
            <input
              type="password"
              {...register('password', {
                required: !isEditing ? 'Contraseña es requerida' : false,
                minLength: {
                  value: 8,
                  message: 'Mínimo 8 caracteres'
                },
                pattern: {
                  value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
                  message: 'Debe contener mayúscula, minúscula y número'
                }
              })}
              className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary-500 text-gray-900"
              autoComplete={isEditing ? 'new-password' : 'password'}
              style={{ pointerEvents: 'auto', userSelect: 'text' }}
            />
            {errors.password && (
              <p className="text-red-500 text-sm mt-1">{errors.password.message}</p>
            )}
          </div>

          {currentUser?.role === 'admin' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Rol *
              </label>
              <select
                {...register('role', { required: 'Rol es requerido' })}
                className="w-full px-3 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                {roles.map((role) => (
                  <option key={role} value={role}>
                    {role.charAt(0).toUpperCase() + role.slice(1)}
                  </option>
                ))}
              </select>
            </div>
          )}

          {isEditing && (
            <div>
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  {...register('isActive')}
                  className="rounded"
                />
                <span className="text-sm font-medium text-gray-700">Activo</span>
              </label>
            </div>
          )}

          <div className="flex justify-end space-x-2 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border rounded hover:bg-gray-50"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={createMutation.isLoading || updateMutation.isLoading}
              className="px-4 py-2 bg-primary-600 text-white rounded hover:bg-primary-700 disabled:opacity-50"
            >
              {createMutation.isLoading || updateMutation.isLoading ? 'Guardando...' : 'Guardar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UserForm;

